import React, { useEffect, useState } from 'react';
import { CalendarDays, Clock3, LoaderCircle, MessageSquareText, Rocket, Sparkles, Trash2, UserPlus, Users, Wallet } from 'lucide-react';
import {
  addOrganizationMemberApi,
  fetchMyPackageApi,
  fetchOrganizationMembersApi,
  removeOrganizationMemberApi
} from '../../api/packages.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useI18n } from '../../hooks/useI18n';
import { useToast } from '../../hooks/useToast';
import { Link } from 'react-router-dom';
import { useSettingsModal } from './SettingsModalContext';
import { settingsControlClass } from './settingsUi';
import { classNames } from '../../utils/classNames';

const getMemberInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const remainingDaysFromExpiry = (expiresAt) => {
  if (!expiresAt) {
    return null;
  }

  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) {
    return null;
  }

  return Math.max(0, Math.ceil((expires - Date.now()) / 86400000));
};

const PackageFact = ({ icon: Icon, label, value, hint, tone = 'default' }) => (
  <article
    className={classNames(
      'rounded-2xl border px-3.5 py-3',
      tone === 'warn'
        ? 'border-amber-200/80 bg-amber-50/80 dark:border-amber-500/25 dark:bg-amber-500/10'
        : tone === 'danger'
          ? 'border-rose-200/80 bg-rose-50/80 dark:border-rose-500/25 dark:bg-rose-500/10'
          : tone === 'accent'
            ? 'border-accent/20 bg-accent-soft/50'
            : 'border-border/70 bg-white/70 dark:bg-slate-950/25'
    )}
  >
    <span
      className={classNames(
        'inline-flex h-8 w-8 items-center justify-center rounded-xl',
        tone === 'warn'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200'
          : tone === 'danger'
            ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200'
            : 'bg-white/80 text-accent dark:bg-slate-950/40'
      )}
    >
      <Icon size={15} aria-hidden />
    </span>
    <p className="mt-3 text-[11px] font-medium text-muted">{label}</p>
    <p className="mt-1 text-sm font-bold tabular-nums tracking-tight text-text">{value}</p>
    {hint ? <p className="mt-1 text-[11px] leading-4 text-muted">{hint}</p> : null}
  </article>
);

export const PackageSettingsPanel = () => {
  const { t, language, locale, formatNumber, formatDate } = useI18n();
  const toast = useToast();
  const { closeSettings } = useSettingsModal();
  const [entitlement, setEntitlement] = useState(null);
  const [members, setMembers] = useState([]);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const current = await fetchMyPackageApi();
      setEntitlement(current);
      if (current?.source === 'Organization') {
        try {
          setMembers(await fetchOrganizationMembersApi());
        } catch {
          setMembers([]);
        }
      }
    } catch {
      setEntitlement(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hasPackage = entitlement?.hasPackage;
  const name = language === 'fa' ? entitlement?.packageNameFa : entitlement?.packageNameEn;
  const messagesRemaining = entitlement?.messagesRemaining ?? 0;
  const publishRemaining = entitlement?.publishSlotsRemaining ?? 0;
  const isOrganization = entitlement?.source === 'Organization';
  const remainingDays = entitlement?.remainingDays ?? remainingDaysFromExpiry(entitlement?.expiresAt);
  const durationDays = Math.max(1, Number(entitlement?.durationDays) || 30);
  const remainingRatio = remainingDays == null ? 0 : Math.min(100, Math.max(0, (remainingDays / durationDays) * 100));
  const remainingTone = remainingDays == null ? 'default' : remainingDays <= 3 ? 'danger' : remainingDays <= 7 ? 'warn' : 'accent';

  const formatToman = (value) => {
    if (value == null || Number.isNaN(Number(value))) {
      return '—';
    }

    const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value));
    return `${formatNumber(formatted)} ${t('walletCurrency')}`;
  };

  const formatPackageDate = (value) => {
    if (!value) {
      return '—';
    }

    try {
      return formatDate(value, {
        dateStyle: 'medium',
        ...(language === 'fa' ? { calendar: 'persian' } : {})
      });
    } catch {
      return formatDate(value, { dateStyle: 'medium' });
    }
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      const next = await addOrganizationMemberApi(phone);
      setMembers(next);
      setPhone('');
      toast.success(t('orgMemberAdded'));
    } catch (error) {
      toast.error(error.message || t('orgMemberAddFailed'));
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center gap-2 text-muted">
        <LoaderCircle size={22} className="animate-spin text-accent" aria-hidden />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!hasPackage ? (
        <div className="relative overflow-hidden rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-5 dark:border-amber-500/20 dark:from-amber-500/10 dark:via-slate-950/20 dark:to-orange-500/5">
          <div className="pointer-events-none absolute -top-8 inset-inline-end-4 h-24 w-24 rounded-full bg-amber-300/30 blur-2xl" aria-hidden />
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200">
            <Sparkles size={18} aria-hidden />
          </span>
          <p className="mt-3 text-sm leading-6 text-amber-950 dark:text-amber-100">
            {entitlement?.upgradeNotice || t('noPackageBanner')}
          </p>
          <Link
            to="/pricing"
            onClick={closeSettings}
            className="mt-4 inline-flex min-h-10 items-center justify-center rounded-xl bg-accent px-4 text-sm font-semibold text-white hover:opacity-95 dark:text-slate-950"
          >
            {t('upgradePlan')}
          </Link>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-white/80 dark:bg-slate-950/30">
          <div
            className="pointer-events-none absolute -top-16 inset-inline-end-0 h-40 w-40 rounded-full bg-accent/15 blur-3xl motion-reduce:hidden"
            aria-hidden
          />
          <div className="relative border-b border-border/70 px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">{t('currentPackageLabel')}</p>
                <h3 className="mt-1 text-lg font-semibold tracking-tight text-text">{name || t('proPlan')}</h3>
                <p className="mt-1 text-xs text-muted">
                  {isOrganization ? t('packageOrganizationSource') : t('packagePersonalSource')}
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                {t('packageActiveBadge')}
              </span>
            </div>

            <div className="mt-4">
              {entitlement?.expiresAt ? (
                <p className="mb-2 text-end text-xs text-muted">{t('packageExpiresOn', { date: formatPackageDate(entitlement.expiresAt) })}</p>
              ) : null}
              <div className="h-2 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-800">
                <div
                  className={classNames(
                    'h-full rounded-full transition-[width] duration-500',
                    remainingTone === 'danger'
                      ? 'bg-rose-500'
                      : remainingTone === 'warn'
                        ? 'bg-amber-500'
                        : 'bg-gradient-to-l from-accent to-teal-400'
                  )}
                  style={{ width: `${remainingRatio}%` }}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={durationDays}
                  aria-valuenow={remainingDays ?? 0}
                  aria-label={t('packageRemainingDaysLabel')}
                />
              </div>
            </div>
          </div>

          <div className="relative grid gap-2.5 p-4 sm:grid-cols-3 sm:p-5">
            <PackageFact
              icon={CalendarDays}
              label={t('packagePurchasedAt')}
              value={formatPackageDate(entitlement?.startsAt)}
            />
            <PackageFact
              icon={Wallet}
              label={t('packagePurchaseAmount')}
              value={formatToman(entitlement?.priceToman)}
            />
            <PackageFact
              icon={Clock3}
              label={t('packageRemainingDaysLabel')}
              value={remainingDays == null ? '—' : t('packageRemainingDaysValue', { count: formatNumber(remainingDays) })}
              tone={remainingTone}
            />
          </div>

          <div className="relative grid gap-2.5 px-4 pb-4 sm:grid-cols-2 sm:px-5 sm:pb-5">
            <article className="rounded-2xl border border-border/70 bg-accent-soft/40 px-4 py-3.5">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/80 text-accent dark:bg-slate-950/40">
                <MessageSquareText size={15} aria-hidden />
              </span>
              <p className="mt-3 text-2xl font-bold tabular-nums tracking-tight text-text">{formatNumber(messagesRemaining)}</p>
              <p className="mt-1 text-xs text-muted">{t('packageMessagesStat')}</p>
            </article>
            <article className="rounded-2xl border border-border/70 bg-white/70 px-4 py-3.5 dark:bg-slate-950/25">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <Rocket size={15} aria-hidden />
              </span>
              <p className="mt-3 text-2xl font-bold tabular-nums tracking-tight text-text">{formatNumber(publishRemaining)}</p>
              <p className="mt-1 text-xs text-muted">{t('packagePublishStat')}</p>
            </article>
          </div>

          {remainingDays != null && remainingDays <= 7 ? (
            <div className="relative border-t border-border/70 px-4 py-3.5 sm:px-5">
              <p className="text-xs leading-5 text-amber-800 dark:text-amber-200">{t('packageRenewHint')}</p>
              <Link
                to="/pricing"
                onClick={closeSettings}
                className="mt-2 inline-flex text-sm font-semibold text-accent hover:underline"
              >
                {t('upgradePlan')}
              </Link>
            </div>
          ) : null}
        </div>
      )}

      {hasPackage && entitlement?.source === 'Organization' ? (
        <section className="overflow-hidden rounded-2xl border border-border/80 bg-white/75 dark:bg-slate-950/30">
          <header className="flex items-start gap-3 border-b border-border/70 px-4 py-3.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <Users size={16} aria-hidden />
            </span>
            <div>
              <h4 className="text-sm font-semibold text-text">{t('orgMembersTitle')}</h4>
              <p className="mt-0.5 text-xs text-muted">{t('orgMembersHint')}</p>
            </div>
          </header>
          <div className="space-y-3 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Input
                  label={t('orgMemberPhone')}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  inputMode="tel"
                />
              </div>
              <Button type="button" onClick={handleAdd} disabled={adding || !phone.trim()} className="sm:mb-0.5">
                {adding ? <LoaderCircle size={16} className="animate-spin" aria-hidden /> : <UserPlus size={16} aria-hidden />}
                {t('orgMemberAdd')}
              </Button>
            </div>
            <ul className="space-y-2">
              {members.map((member) => {
                const memberName = member.fullName || member.FullName || member.phoneNumber || member.PhoneNumber;
                const pending = member.status === 'pending' || member.Status === 'pending';

                return (
                  <li
                    key={member.userId || member.UserId}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-border/70 px-3 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-xs font-bold text-accent">
                        {getMemberInitials(memberName)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-text">{memberName}</span>
                        {pending ? <span className="text-xs text-amber-600 dark:text-amber-300">{t('orgMemberPending')}</span> : null}
                      </span>
                    </div>
                    {!member.isOwner && !member.IsOwner ? (
                      <button
                        type="button"
                        className={classNames(
                          settingsControlClass,
                          'inline-flex h-9 items-center gap-1 px-2.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10'
                        )}
                        onClick={() => removeOrganizationMemberApi(member.userId || member.UserId).then(load)}
                      >
                        <Trash2 size={13} aria-hidden />
                        {t('orgMemberRemove')}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
};
